/**
 * ServiceGrossBreakdown Component
 *
 * Two pie charts for service department analysis:
 * 1. Gross by Pay Type (Customer, Warranty, Internal, Other)
 * 2. Controllable Expenses as % of total gross (Advertising, Policy, Warranty Rejection, Loss Diag)
 */

import React, { useMemo } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { WrenchScrewdriverIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { OpenROAgeBuckets, AppointmentDayBucket } from './ServiceAdvisorPerformance/types';

export interface ServiceGrossByPayType {
  customer: number;
  warranty: number;
  internal: number;
  other: number;
}

export interface ServiceControllableExpense {
  accountCode: string;
  description: string;
  amount: number;
}

interface ServiceGrossBreakdownProps {
  serviceGrossByPayType: ServiceGrossByPayType | null;
  serviceControllableExpenses: ServiceControllableExpense[] | null;
  /** Total service gross from the blue tile (squeezed, used as denominator for expense %) */
  totalServiceGross: number;
  year: number;
  month: number;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  closedROCount?: number;
  openROCount?: number;
  openROAgeBuckets?: OpenROAgeBuckets;
  /** Forward-looking 5-day appointments snapshot from the nightly loader. */
  appointmentsNext5Days?: AppointmentDayBucket[];
}

// --- Pay Type Chart Config ---
const PAY_TYPE_CONFIG = [
  { key: 'customer' as const, label: 'Customer', color: '#3b82f6' },
  { key: 'warranty' as const, label: 'Warranty', color: '#f59e0b' },
  { key: 'internal' as const, label: 'Internal', color: '#10b981' },
  { key: 'other' as const, label: 'Other', color: '#9ca3af' },
];

// --- Expense Chart Config ---
const EXPENSE_COLORS: Record<string, string> = {
  '062D': '#ef4444',   // red-500 - Advertising
  '067D': '#f97316',   // orange-500 - Policy
  '069D': '#8b5cf6',   // violet-500 - Warranty Rejection
  '069DL': '#ec4899',  // pink-500 - Loss Diag
};
const REMAINING_GROSS_COLOR = '#e5e7eb'; // gray-200

// --- Open RO Age Bucket Config ---
const AGE_BUCKET_CONFIG = [
  { key: 'under30' as const, label: '0–29 days', color: '#22c55e' },   // green-500
  { key: 'over30' as const, label: '30–59 days', color: '#eab308' },   // yellow-500
  { key: 'over60' as const, label: '60–89 days', color: '#f97316' },   // orange-500
  { key: 'over90' as const, label: '90–119 days', color: '#ef4444' },  // red-500
  { key: 'over120' as const, label: '120+ days', color: '#991b1b' },   // red-900
];

// --- Shared Helpers ---
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatCurrencyShort(value: number): string {
  if (Math.abs(value) >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (Math.abs(value) >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
}

function getMonthName(month: number): string {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return months[month - 1] || '';
}

// --- Pay Type Tooltip ---
const PayTypeTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 max-w-xs">
        <p className="font-semibold text-gray-900 mb-1">{data.label}</p>
        <p className="text-sm text-gray-600">
          Gross:{' '}
          <span className="font-medium text-blue-600">
            {formatCurrency(data.value)}
          </span>
        </p>
        <p className="text-sm text-gray-600">
          Share: <span className="font-medium">{data.percentage.toFixed(1)}%</span>
        </p>
      </div>
    );
  }
  return null;
};

// --- Expense Tooltip ---
const ExpenseTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    if (data.isRemainder) return null;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 max-w-xs">
        <p className="font-semibold text-gray-900 mb-1">{data.label}</p>
        <p className="text-xs text-gray-400 mb-1">{data.accountCode}</p>
        <p className="text-sm text-gray-600">
          Expense:{' '}
          <span className="font-medium text-red-600">
            {formatCurrency(data.rawAmount)}
          </span>
        </p>
        <p className="text-sm text-gray-600">
          % of Gross: <span className="font-medium">{data.pctOfGross.toFixed(2)}%</span>
        </p>
      </div>
    );
  }
  return null;
};

// --- Pay Type Legend ---
const PayTypeLegend = (props: any) => {
  const { payload } = props;
  if (!payload) return null;

  return (
    <div className="flex flex-col justify-center gap-2 pl-2">
      {payload.map((entry: any, index: number) => {
        const data = entry.payload;
        return (
          <div key={index} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-sm flex-shrink-0"
              style={{ backgroundColor: entry.color }}
            />
            <div className="text-sm">
              <span className="text-gray-700">{data.label}</span>
              <span className="text-gray-500 ml-1">
                {formatCurrencyShort(data.value)}
              </span>
              <span className="text-gray-400 ml-1">
                ({data.percentage.toFixed(0)}%)
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// --- Expense Legend ---
const ExpenseLegend = (props: any) => {
  const { payload } = props;
  if (!payload) return null;

  return (
    <div className="flex flex-col justify-center gap-2 pl-2">
      {payload
        .filter((entry: any) => !entry.payload.isRemainder)
        .map((entry: any, index: number) => {
          const data = entry.payload;
          return (
            <div key={index} className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-sm flex-shrink-0"
                style={{ backgroundColor: entry.color }}
              />
              <div className="text-sm">
                <span className="text-gray-700">{data.label}</span>
                <span className="text-gray-500 ml-1">
                  {formatCurrencyShort(data.rawAmount)}
                </span>
                <span className="text-gray-400 ml-1">
                  ({data.pctOfGross.toFixed(1)}%)
                </span>
              </div>
            </div>
          );
        })}
    </div>
  );
};

export const ServiceGrossBreakdown: React.FC<ServiceGrossBreakdownProps> = ({
  serviceGrossByPayType,
  serviceControllableExpenses,
  totalServiceGross,
  year,
  month,
  collapsed = false,
  onToggleCollapse,
  closedROCount,
  openROCount,
  openROAgeBuckets,
  appointmentsNext5Days,
}) => {
  const appointmentsTotal = (appointmentsNext5Days ?? []).reduce((s, d) => s + d.count, 0);
  const appointmentsCapacityTotal = (appointmentsNext5Days ?? []).reduce(
    (s, d) => s + (d.capacity ?? 0),
    0
  );
  const hasCapacity = appointmentsCapacityTotal > 0;
  // RO progress bar calculations
  const totalROs = (closedROCount ?? 0) + (openROCount ?? 0);
  const closedPercent = totalROs > 0 ? ((closedROCount ?? 0) / totalROs) * 100 : 0;
  const openPercent = totalROs > 0 ? ((openROCount ?? 0) / totalROs) * 100 : 0;
  const openRate = (closedROCount ?? 0) > 0 ? ((openROCount ?? 0) / (closedROCount ?? 0)) * 100 : 0;

  // Build age bucket segments for the open portion of the bar
  const ageBucketSegments = useMemo(() => {
    if (!openROAgeBuckets || (openROCount ?? 0) === 0) return null;
    const openCount = openROCount ?? 0;
    return AGE_BUCKET_CONFIG
      .filter(b => openROAgeBuckets[b.key] > 0)
      .map(b => ({
        ...b,
        count: openROAgeBuckets[b.key],
        // Each bucket's width is its share of the total open portion
        widthPercent: (openROAgeBuckets[b.key] / openCount) * openPercent,
      }));
  }, [openROAgeBuckets, openROCount, openPercent]);

  // --- Pay Type Chart Data ---
  const payTypeChartData = useMemo(() => {
    if (!serviceGrossByPayType) return [];

    const total = PAY_TYPE_CONFIG.reduce(
      (sum, pt) => sum + (serviceGrossByPayType[pt.key] || 0),
      0
    );
    if (total === 0) return [];

    return PAY_TYPE_CONFIG
      .map(pt => ({
        key: pt.key,
        label: pt.label,
        value: serviceGrossByPayType[pt.key] || 0,
        percentage: ((serviceGrossByPayType[pt.key] || 0) / total) * 100,
        color: pt.color,
      }))
      .filter(d => d.value !== 0);
  }, [serviceGrossByPayType]);

  const payTypeTotal = useMemo(() => {
    if (!serviceGrossByPayType) return 0;
    return PAY_TYPE_CONFIG.reduce(
      (sum, pt) => sum + (serviceGrossByPayType[pt.key] || 0),
      0
    );
  }, [serviceGrossByPayType]);

  // --- Expense Chart Data ---
  const expenseChartData = useMemo(() => {
    if (!serviceControllableExpenses || totalServiceGross <= 0) return [];

    const expenseSlices = serviceControllableExpenses
      .filter(e => e.amount !== 0)
      .map(e => ({
        key: e.accountCode,
        accountCode: e.accountCode,
        label: e.description,
        value: Math.abs(e.amount), // Use abs for pie sizing
        rawAmount: e.amount,
        pctOfGross: (e.amount / totalServiceGross) * 100,
        color: EXPENSE_COLORS[e.accountCode] || '#6b7280',
        isRemainder: false,
      }));

    if (expenseSlices.length === 0) return [];

    const totalExpenses = expenseSlices.reduce((sum, e) => sum + e.value, 0);
    const remainingGross = Math.max(0, totalServiceGross - totalExpenses);

    return [
      ...expenseSlices,
      {
        key: 'remaining',
        accountCode: '',
        label: 'Remaining Gross',
        value: remainingGross,
        rawAmount: remainingGross,
        pctOfGross: (remainingGross / totalServiceGross) * 100,
        color: REMAINING_GROSS_COLOR,
        isRemainder: true,
      },
    ];
  }, [serviceControllableExpenses, totalServiceGross]);

  const totalExpenseAmount = useMemo(() => {
    if (!serviceControllableExpenses) return 0;
    return serviceControllableExpenses.reduce((sum, e) => sum + e.amount, 0);
  }, [serviceControllableExpenses]);

  const hasExpenseData = expenseChartData.length > 0;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div
        className={`px-4 py-3 ${collapsed ? '' : 'border-b border-gray-200'} ${onToggleCollapse ? 'cursor-pointer select-none hover:bg-gray-50 transition-colors' : ''}`}
        onClick={onToggleCollapse}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <WrenchScrewdriverIcon className="h-5 w-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">
              Service Department Analysis
            </h3>
            <p className="text-sm text-gray-500">
              {getMonthName(month)} {year}
            </p>
          </div>
          {onToggleCollapse && (
            <ChevronDownIcon
              className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${collapsed ? '' : 'rotate-180'}`}
            />
          )}
        </div>
      </div>

      {/* Content */}
      {!collapsed && <div className="p-4">
        {/* RO Progress Bar */}
        {(closedROCount ?? 0) > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Repair Orders</span>
              <div className="flex items-center gap-3 text-sm">
                <span className="font-semibold text-blue-700">{closedROCount} Closed</span>
                <span className="text-gray-400">|</span>
                <span className="font-semibold text-amber-600">{openROCount ?? 0} Open</span>
                <span className="text-gray-400">|</span>
                <span className="text-gray-600">{openRate.toFixed(1)}% Open Rate</span>
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden flex">
              {/* Closed ROs - blue segment */}
              <div
                className="bg-blue-500 h-3 transition-all duration-500"
                style={{ width: `${closedPercent}%` }}
                title={`${closedROCount} Closed ROs`}
              />
              {/* Open ROs - age-bucketed segments or single amber fallback */}
              {ageBucketSegments ? (
                ageBucketSegments.map(seg => (
                  <div
                    key={seg.key}
                    className="h-3 transition-all duration-500"
                    style={{ width: `${seg.widthPercent}%`, backgroundColor: seg.color }}
                    title={`${seg.count} ROs ${seg.label}`}
                  />
                ))
              ) : (openROCount ?? 0) > 0 ? (
                <div
                  className="bg-amber-500 h-3 transition-all duration-500"
                  style={{ width: `${openPercent}%` }}
                  title={`${openROCount} Open ROs`}
                />
              ) : null}
            </div>
            {/* Age bucket legend (only when bucket data is available) */}
            {ageBucketSegments && ageBucketSegments.length > 0 && (
              <div className="flex flex-wrap items-center gap-3 mt-2">
                <span className="text-xs text-gray-500">Open RO Age:</span>
                {ageBucketSegments.map(seg => (
                  <div key={seg.key} className="flex items-center gap-1">
                    <div
                      className="w-2.5 h-2.5 rounded-sm"
                      style={{ backgroundColor: seg.color }}
                    />
                    <span className="text-xs text-gray-600">
                      {seg.count} ({seg.label})
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Service Appointments — next 5 days (forward-looking snapshot from nightly loader) */}
        {appointmentsNext5Days && appointmentsNext5Days.length > 0 && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                Service Appointments — next 5 days
                <span className="ml-1 text-xs font-normal text-gray-500">(excludes cancelled appts)</span>
              </span>
              <span className="text-sm text-gray-700">
                {hasCapacity ? (
                  <>
                    <span className="font-semibold text-emerald-700">{appointmentsTotal}</span>
                    <span className="text-gray-500"> booked / </span>
                    <span className="font-semibold text-emerald-700">{appointmentsCapacityTotal}</span>
                    <span className="text-gray-500"> max</span>
                  </>
                ) : (
                  <>
                    Total:{' '}
                    <span className="font-semibold text-emerald-700">{appointmentsTotal}</span>
                  </>
                )}
              </span>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {appointmentsNext5Days.map((d) => (
                <div
                  key={d.date}
                  className="flex flex-col items-center justify-center bg-white border border-emerald-100 rounded-md py-1.5"
                  title={
                    d.capacity
                      ? `${d.date} — Booked: ${d.count}, Max: ${d.capacity}`
                      : d.date
                  }
                >
                  <span className="text-xs uppercase tracking-wide text-gray-500">{d.dayLabel}</span>
                  <span className="text-base font-semibold text-emerald-700 leading-tight">
                    {d.capacity ? `${d.count}/${d.capacity}` : d.count}
                  </span>
                  {d.capacity ? (
                    <span className="text-[10px] text-gray-400 leading-tight">booked / max</span>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left column: Gross by Pay Type */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Gross by Pay Type</h4>
            {payTypeChartData.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-gray-500 text-sm">
                No service gross data available
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={payTypeChartData}
                      cx="40%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="label"
                    >
                      {payTypeChartData.map((entry, index) => (
                        <Cell key={`pt-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<PayTypeTooltip />} />
                    <Legend
                      layout="vertical"
                      align="right"
                      verticalAlign="middle"
                      content={PayTypeLegend}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="text-center text-sm text-gray-500 -mt-2">
                  Total: {formatCurrency(payTypeTotal)}
                </div>
              </div>
            )}
          </div>

          {/* Right column: Controllable Expenses as % of Gross */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Controllable Expenses</h4>
            {!hasExpenseData ? (
              <div className="h-64 flex items-center justify-center text-gray-500 text-sm">
                No expense data available
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={expenseChartData}
                      cx="40%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={1}
                      dataKey="value"
                      nameKey="label"
                    >
                      {expenseChartData.map((entry, index) => (
                        <Cell
                          key={`exp-${index}`}
                          fill={entry.color}
                          stroke={entry.isRemainder ? '#d1d5db' : undefined}
                          strokeWidth={entry.isRemainder ? 1 : 0}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<ExpenseTooltip />} />
                    <Legend
                      layout="vertical"
                      align="right"
                      verticalAlign="middle"
                      content={ExpenseLegend}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="text-center text-sm text-gray-500 -mt-2">
                  Total: {formatCurrency(totalExpenseAmount)} ({totalServiceGross > 0 ? ((totalExpenseAmount / totalServiceGross) * 100).toFixed(1) : '0'}% of gross)
                </div>
              </div>
            )}
          </div>
        </div>
      </div>}
    </div>
  );
};
